import { Link } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { TryItFree } from '@/features/Pricing/Components/TryItFree';
// import { GettingStarted } from './GettingStarted';

export const HomepageSalesLetter = () => {
  const fsboQuestionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Check if URL has hash for FSBO question
    if (window.location.hash === '#fsbo-question') {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        fsboQuestionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
        // Highlight the question (permanently)
        fsboQuestionRef.current?.classList.add('highlight-fsbo');
      }, 100);
    }
  }, []);

  const handleFsboClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    window.location.hash = '#fsbo-question';
    fsboQuestionRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
    // Highlight the question (permanently)
    fsboQuestionRef.current?.classList.add('highlight-fsbo');
  };

  const handleScrollToTop = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    // Clear the hash from URL
    if (window.history?.replaceState) {
      window.history.replaceState(
        null,
        '',
        window.location.pathname + window.location.search
      );
    }

    // Scroll to top of page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="w-full">
      {/* Partner Logos Section */}
      <section className="bg-white py-12 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="flex flex-wrap items-center justify-center gap-8 opacity-30">
            {[
              { src: '/images/realtor-canada.png', alt: 'REALTOR.ca' },
              { src: '/images/brokerbay.png', alt: 'BrokerBay' },
              { src: '/images/canada.jpg', alt: 'Canada' },
              { src: '/images/proptx.png', alt: 'PropTx', padding: '10px' },
              { src: '/images/crea.png', alt: 'CREA' }
            ].map((logo, i) => (
              <img
                key={i}
                src={logo.src}
                alt={logo.alt}
                className="h-12 object-contain"
                style={{ padding: logo.padding }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Header/Hero Section */}
      <section className="bg-white py-16 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="flex flex-wrap items-center">
            {/* Main Content */}
            <div className="w-full lg:w-1/2 mb-8 lg:mb-0 lg:pr-8">
              <h1 className="text-3xl lg:text-4xl xl:text-4xl font-bold text-black mb-6 leading-tight">
                &quot;... search my name on REALTOR.ca and see my active
                listings for yourself...&quot;
              </h1>

              <p className="text-xl text-gray-800 mb-6 leading-relaxed font-semibold">
                How one Ontario agent generated{' '}
                <strong className="text-blue-600">584 qualified leads</strong> —
                mostly high-intent buyer and rental{' '}
                <strong className="text-blue-600">
                  REALTOR.ca showing requests
                </strong>{' '}
                — alongside{' '}
                <strong className="text-blue-600">
                  177 FSBO mere postings
                </strong>{' '}
                in six months with BrokerMax.
              </p>

              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                These aren&apos;t forced popups or cold ad clicks. They&apos;re
                people who requested showings and expected a call — plus
                optional mere postings for sellers who want REALTOR.ca exposure
                without full representation (your brokerage rules apply).
              </p>

              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                During a tough stretch in the market (July 2025 to January
                2026), this agent turned BrokerMax into a steady pipeline:{' '}
                <strong>584 buyer and rental leads</strong> from{' '}
                <strong>177 FSBO listings</strong> over six months, with roughly{' '}
                <strong>10–15% close rates</strong> on delivered leads when
                follow-up was fast.
              </p>

              {/* Key Metrics */}
              <div className="grid grid-cols-3 gap-6 mb-8 p-6 bg-gray-50 rounded-lg border border-gray-200">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-1">
                    584
                  </div>
                  <div className="text-sm text-gray-600">Total Leads</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-1">
                    177
                  </div>
                  <div className="text-sm text-gray-600">
                    <a
                      href="#fsbo-question"
                      onClick={handleFsboClick}
                      className="inline-flex items-center ml-2 text-blue-600 hover:text-blue-700 transition-colors"
                      aria-label="Learn more about FSBO listings"
                    >
                      FSBO Listings &nbsp;
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </a>
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-1">6</div>
                  <div className="text-sm text-gray-600">Months</div>
                </div>
              </div>

              {/* CTA to Full Case Study */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <Link
                  to="/results"
                  className="btn--blue-400 hover--blue-500 font-semibold px-8 py-4 rounded transition-colors inline-block text-center"
                >
                  Read the Full Case Study &#187;
                </Link>
              </div>
            </div>

            {/* Image */}
            <div className="w-full lg:w-1/2">
              <div className="bg-gray-200 rounded-lg aspect-[4/5] flex items-center justify-center overflow-hidden">
                <img
                  src="/images/showings.png"
                  alt="Realtor.ca Showings"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Section with Image Background */}
      <section className="bg-gray-100 py-20 px-4 relative overflow-hidden">
        {/* Parallax Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-fixed bg-no-repeat"
          style={{
            backgroundImage:
              'url(https://easywebinar-site.easywebinar.com/images/amy-porter-image.jpg)',
            backgroundAttachment: 'fixed',
            backgroundPosition: 'center',
            backgroundSize: 'cover'
          }}
        ></div>

        <div className="container mx-auto max-w-7xl relative z-10">
          {/* Testimonial Box and Image Layout */}
          <div className="flex flex-col lg:flex-row items-start gap-8">
            {/* Testimonial Box - Left Side */}
            <div
              className="w-full lg:w-2/3 rounded-lg p-8 lg:p-12 relative"
              style={{ backgroundColor: 'rgba(0, 27, 47, 0.72)' }}
            >
              {/* Star Rating */}
              <div className="flex mb-6">
                {[...Array(5)].map((_, i) => (
                  <svg
                    key={i}
                    className="w-6 h-6 text-yellow-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>

              {/* Testimonial Text */}
              <p className="text-xl text-white mb-8 leading-relaxed">
                &ldquo; I was a solo agent burning thousands on Google Ads, lead
                brokers and direct mail... BrokerMax literally changed
                everything for me. I even had to build my team of 3 agents
                almost overnight because it was too much for me to handle on my
                own.
                <span className="font-bold">
                  {' '}
                  Now, I always have at least 30 listings up every month, even
                  in the &quot;slow&quot; winter. But the real magic is the
                  buyer and rental leads... Search my name on REALTOR.ca and see
                  my active listings for yourself.
                </span>
                &rdquo;
              </p>

              {/* Attribution */}
              <div className="mb-6">
                <div
                  data-nosnippet
                  className="text-blue-400 text-xl font-semibold mb-2"
                >
                  Shamairah Noufaro
                </div>
                <div className="text-gray-300 text-sm">
                  Real estate agent in Ontario
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How Would You Use Section */}
      <section className="bg-white py-20 px-4">
        <div className="container mx-auto max-w-7xl">
          <h2 className="text-4xl font-bold text-black mb-4 text-left">
            LIMITED CAPACITY BY MARKET
          </h2>
          <p className="text-lg text-gray-700 mb-12 text-left max-w-3xl">
            REALTOR.ca showing requests are finite each week. We cap enrollment
            by territory so agents aren&apos;t flooding the same ZIP or FSA —
            shared and exclusive lead types included — inventory has to be
            managed carefully.
            <br />
            <br />
            You can choose shared leads (delivered to up to two agents) or
            exclusive leads (one agent only); see our FAQ for details.
          </p>

          {/* Feature List */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            <div className="space-y-4">
              <div className="flex items-start">
                <svg
                  className="w-6 h-6 text-green-600 mr-3 mt-1 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-gray-700">
                  Ontario: 2 spots available
                </span>
              </div>
              <div className="flex items-start">
                <svg
                  className="w-6 h-6 text-green-600 mr-3 mt-1 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-gray-700">
                  Alberta: 3 spots available
                </span>
              </div>
              <div className="flex items-start">
                <svg
                  className="w-6 h-6 text-green-600 mr-3 mt-1 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-gray-700">
                  British Columbia: Coming soon (5 spots available)
                </span>
              </div>
            </div>
          </div>

          {/* Pricing Toggle and CTA */}
          <div className="text-center">
            {/* <p className="text-sm text-gray-600 mb-6">Save additional 25% with annual billing</p> */}
            <div className="flex items-center justify-center gap-8">
              <div>
                <div className="text-4xl font-bold text-black mb-1">
                  Ready to get started?
                </div>
              </div>
              <Link
                to="/schedule-call"
                className="btn !rounded-[4px] btn--blue-400 hover--blue-500"
              >
                Schedule a Call with Us &#187;
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-4 relative">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage:
              'url(https://easywebinar-site.easywebinar.com/images/what-get-bg.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="container mx-auto max-w-7xl relative z-10">
          <h2 className="text-6xl font-bold text-white mb-6 text-left">
            Here&apos;s what you get
          </h2>
          <p className="text-lg text-white text-left max-w-3xl">
            Individual <strong>REALTOR.ca showing-request</strong> buyer and
            rental leads — <strong>shared or exclusive options</strong> — plus
            optional mere postings where your brokerage allows it.
          </p>
        </div>
        {/* 3 Features */}

        <div className="container mx-auto max-w-7xl relative z-10">
          {/* Feature Boxes */}
          <div className="relative py-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Feature 1 */}
              <div className="bg-white rounded-lg p-8 shadow-lg">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                  <svg
                    className="w-8 h-8 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-black mb-4">
                  <span className="underline decoration-2 underline-offset-4">
                    Buyer leads
                  </span>
                  , ready to buy
                </h3>
                <span className="inline-block bg-green-50 border border-green-200 rounded-md px-3 py-2 mb-2 text-gray-800">
                  <span className="font-semibold">
                    83% are looking to buy within 45-days.
                  </span>
                </span>
                <p className="text-gray-700 leading-relaxed">
                  Our high-intent Buyer leads are from REALTOR.ca and have
                  requested a showing to see a property.
                  <br />
                  <br />
                  <Link
                    to="/pricing"
                    className="text-blue-600 font-medium underline hover:text-blue-700"
                  >
                    Calculate earning potential &#187;
                  </Link>
                </p>
              </div>

              {/* Feature 2 */}
              <div className="bg-white rounded-lg p-8 shadow-lg">
                <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mb-6">
                  <svg
                    className="w-8 h-8 text-pink-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-black mb-4">
                  <span className="underline decoration-2 underline-offset-4">
                    Rental leads
                  </span>
                  , ready to rent
                </h3>
                <span className="inline-block bg-green-50 border border-green-200 rounded-md px-3 py-2 mb-2 font-semibold text-gray-800">
                  91% are looking to rent within 30-days
                </span>
                <p className="text-gray-700 leading-relaxed">
                  Our high-intent Rental leads are also from REALTOR.ca and have
                  requested a showing to see a property.
                  <br />
                  <br />
                  <Link
                    to="/pricing"
                    className="text-blue-600 font-medium underline hover:text-blue-700"
                  >
                    Calculate earning potential &#187;
                  </Link>
                </p>
              </div>

              {/* Feature 3 */}
              <div className="bg-white rounded-lg p-8 shadow-lg">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-6">
                  <svg
                    className="w-8 h-8 text-purple-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-black mb-4">
                  <span className="underline decoration-2 underline-offset-4">
                    Mere postings
                  </span>
                  , to convert
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {/* <span className="inline-block bg-yellow-50 border border-yellow-200 rounded-md px-3 py-2 mb-2 font-semibold text-gray-800">
                    Brokerage must allow for Mere Postings!
                  </span>
                  <br /> */}
                  Property owners who want their listing on Realtor.ca through
                  you,{' '}
                  <span className="underline decoration-2 underline-offset-4">
                    without representation
                  </span>
                  . With the opportunity to convert them to full representation.
                  <br />
                  <br />
                  <a
                    href="#fsbo-question"
                    onClick={handleFsboClick}
                    className="inline-flex items-center ml-2 text-blue-600 hover:text-blue-700 transition-colors"
                    aria-label="Learn more about FSBO listings"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    &nbsp; What does this mean?
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partner Logos Section */}
      <section className="bg-white py-12 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="flex flex-wrap items-center justify-center gap-8 opacity-30">
            {[
              { src: '/images/realtor-canada.png', alt: 'REALTOR.ca' },
              { src: '/images/brokerbay.png', alt: 'BrokerBay' },
              { src: '/images/canada.jpg', alt: 'Canada' },
              { src: '/images/proptx.png', alt: 'PropTx', padding: '10px' },
              { src: '/images/crea.png', alt: 'CREA' }
            ].map((logo, i) => (
              <img
                key={i}
                src={logo.src}
                alt={logo.alt}
                className="h-12 object-contain"
                style={{ padding: logo.padding }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Intent source vs interruption leads */}
      <section className="border-y border-slate-200 bg-slate-50 py-16 px-4">
        <div className="container mx-auto max-w-5xl">
          <h2 className="text-3xl font-bold text-slate-900 mb-3 text-center">
            Showing-request intent — not popup traffic
          </h2>
          <p className="text-center text-slate-600 mb-10 max-w-2xl mx-auto">
            Many lead sources rely on popups, forced forms, and cold ad clicks.
            BrokerMax focuses on <strong>REALTOR.ca Request a Showing</strong>{' '}
            activity — people who already picked a listing and want next steps.
            You can purchase <strong>shared</strong> or{' '}
            <strong>exclusive</strong> delivery depending on offer and
            availability (see FAQ).
          </p>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-bold text-slate-800 mb-2">
                Low-intent interruption leads
              </h3>
              <ul className="list-disc pl-5 text-slate-700 space-y-2 text-sm leading-relaxed">
                <li>
                  Popups, remarketing, broad ads — weak timing vs showing-ready
                  intent
                </li>
                <li>Often earlier in the funnel or accidental clicks</li>
                <li>Harder to justify fast outreach spend</li>
              </ul>
            </div>
            <div className="rounded-xl border-2 border-blue-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-bold text-slate-900 mb-2">
                BrokerMax — REALTOR.ca showing requests
              </h3>
              <ul className="list-disc pl-5 text-slate-700 space-y-2 text-sm leading-relaxed">
                <li>
                  Leads tied to <strong>Request a Showing</strong> on a real
                  listing
                </li>
                <li>
                  Buy individually; choose shared (up to 2 agents) or exclusive
                  (1 agent) where offered
                </li>
                <li className="text-slate-600">
                  Wallet + territory targeting in the product — see pricing for
                  bundles.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Common Questions Section */}
      <section className="bg-white pt-20 pb-10 px-4">
        <div className="container mx-auto">
          <h2 className="text-4xl font-bold text-black mb-4 text-center">
            Questions & Answers
          </h2>
          <p className="text-lg text-gray-600 mb-12 text-center">
            Everything you need to know about BrokerMax
          </p>

          <div className="max-w-[700px] mx-auto space-y-8">
            <div>
              <h3 className="text-xl font-bold text-black mb-3">
                Where do your leads come from?
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Our leads come from actual showing requests on Realtor.ca -
                people who found a property, clicked &quot;Request
                Showing,&quot; and are actively waiting for an agent to contact
                them. These aren&apos;t form-fills to unlock photos or purchased
                data. They&apos;re high-intent buyers and tenants who expect
                your call.
              </p>
            </div>

            <div
              id="fsbo-question"
              ref={fsboQuestionRef}
              className="transition-all duration-500 rounded-lg"
            >
              <h3 className="text-xl font-bold text-black mb-3">
                What is an FSBO listing?
              </h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                As a bonus to BrokerMax subscribers, we connect you with
                property owners who want their listing on Realtor.ca without
                representation (FSBO; For Sale By Owner) - CREA calls this a
                Mere Posting. We gather their listing information and provide it
                to you to post as a mere listing — meaning you don&apos;t
                receive a listing commission upfront.
              </p>
              <p className="text-gray-700 leading-relaxed">
                While BrokerMax primarily delivers buyer and rental leads, an
                added benefit is that a percentage of these FSBO sellers will
                convert to full representation once they realize they need your
                expertise to close the deal.
              </p>
              <p className="text-gray-700 leading-relaxed font-semibold">
                ✅ If your brokerage does not allow for Mere Postings, you can
                still receive Buyer and Rental leads.
              </p>
              <div className="mt-4">
                <button
                  type="button"
                  onClick={handleScrollToTop}
                  className="inline-flex items-center text-[#1680fb] hover:text-[#1268d4] transition-colors font-medium text-[0.9375rem] bg-transparent border-0 p-0 cursor-pointer"
                >
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 10l7-7m0 0l7 7m-7-7v18"
                    />
                  </svg>
                  Scroll back to top
                </button>
              </div>
            </div>

            <div id="shared-exclusive-question">
              <h3 className="text-xl font-bold text-black mb-3">
                Do you offer shared and exclusive leads?
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Yes. We offer <strong>both</strong>. <strong>Shared</strong>{' '}
                leads are distributed to up to <strong>two (2)</strong> agents.{' '}
                <strong>Exclusive</strong> leads are distributed to{' '}
                <strong>one (1)</strong> agent only. Availability and pricing
                vary by lead type and market.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-bold text-black mb-3">
                What markets do you serve?
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Currently we serve both Ontario and Alberta. British Columbia is
                coming soon.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-bold text-black mb-3">
                How quickly will I start receiving leads?
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Leads begin flowing within 24-48 hours of your subscription
                activation. FSBO listings are assigned as they become available,
                typically within the first week.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-bold text-black mb-3">
                Do I need special software or CRM integration?
              </h3>
              <p className="text-gray-700 leading-relaxed">
                No. Leads are delivered via email and/or to your existing CRM if
                you provide integration details. We support most major real
                estate CRMs.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-bold text-black mb-3">
                What if the leads don&apos;t convert?
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Conversion depends on your follow-up speed and sales process. We
                deliver qualified, high-intent leads — but we can&apos;t control
                how quickly you respond or how well you close. Industry average
                close rates are 7-15%; our clients typically see 10-15% with
                proper follow-up.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-bold text-black mb-3">
                Can I cancel anytime?
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Yes. Plans are month-to-month with no long-term contracts. If
                you cancel, you&apos;ll continue receiving leads through the end
                of your billing cycle.
              </p>
            </div>
          </div>

          {/* More Questions Link */}
          <div className="text-center mt-12">
            <p className="text-lg text-gray-700 more-questions-txt bg--white-400 rounded-[100px] inline-block px-[46px] py-[22px] lg:max-xl:px-11 lg:max-xl:py-[18px] md:max-lg:px-[42px] md:max-lg:py-[18px] sm:max-md:px-[42px] sm:max-md:py-[18px] xsm:max-sm:px-9 xsm:max-sm:py-[18px]">
              Curious to learn more? &nbsp;
              <Link
                to="/pricing"
                className="text-blue-600 font-medium underline hover:text-blue-700"
              >
                View Pricing
              </Link>
              &nbsp; or &nbsp;
              <Link
                to="/schedule-call"
                className="text-blue-600 font-medium underline hover:text-blue-700"
              >
                Schedule a Call &#187;
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <TryItFree />

      {/* Getting Started Section */}
      {/* <GettingStarted /> */}
    </div>
  );
};
